import React, { useCallback, useRef, useState } from 'react';
import '@progress/kendo-theme-default/dist/all.css';
import { Button } from '@progress/kendo-react-buttons';
import { FormBuilder, FormLibrary } from '@fulcrum/form-embedded';
import type { Field, FormBuilderCallbacks, FormVersion } from '@fulcrum/form-embedded';
import { useFormStore } from './mockApi';
import { importFormFromScreenshot } from './ai/screenshotImport';
import './App.css';

type FormStore = ReturnType<typeof useFormStore>;

function AppHeader() {
  return (
    <header className="app-header">
      <div className="app-logo">
        <span className="app-logo-icon">📋</span>
        <span className="app-logo-text">Fulcrum Form</span>
        <span className="app-logo-badge">Example Host App</span>
      </div>
      <div className="app-header-right">
        <span className="app-subtitle">
          Demonstrating <code>@fulcrum/form-embedded</code>
        </span>
        <span className="app-header-sep" />
        <a
          className="app-header-link"
          href="https://www.telerik.com/kendo-react-ui"
          target="_blank"
          rel="noopener noreferrer"
        >
          Powered by KendoReact
        </a>
        <span className="app-header-sep" />
        <a
          className="app-header-link app-header-link--github"
          href="https://github.com/srupke/Fulcrum.Form.Embedded"
          target="_blank"
          rel="noopener noreferrer"
          title="View source on GitHub"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" fill="currentColor">
            <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
          </svg>
          GitHub
        </a>
      </div>
    </header>
  );
}

// ─── Events panel ───────────────────────────────────────────────────────────

interface LogEntry {
  id: number;
  time: string;
  type: string;
  detail: string;
  payload?: unknown;
}

const LOG_TYPE_COLORS: Record<string, string> = {
  SAVE_DRAFT: '#86efac',
  PUBLISH_VERSION: '#fbbf24',
  CREATE_VERSION: '#60a5fa',
  RESTORE_VERSION: '#a78bfa',
  ARCHIVE_VERSION: '#f87171',
  FIELD_SELECTED: '#94a3b8',
  DIRTY_CHANGED: '#38bdf8',
  IMPORT_SCREENSHOT: '#c084fc',
};

let logCounter = 0;

const PANEL_MIN = 200;
const PANEL_MAX = 480;
const PANEL_DEFAULT = 300;

function EventsPanel({ log, onClear }: { log: LogEntry[]; onClear: () => void }) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT);
  const [collapsed, setCollapsed] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (!panelRef.current) return;
    const startX = e.clientX;
    const startWidth = panelRef.current.offsetWidth;

    const onMouseMove = (ev: MouseEvent) => {
      const w = Math.min(PANEL_MAX, Math.max(PANEL_MIN, startWidth + ev.clientX - startX));
      if (panelRef.current) panelRef.current.style.width = `${w}px`;
    };
    const onMouseUp = (ev: MouseEvent) => {
      const w = Math.min(PANEL_MAX, Math.max(PANEL_MIN, startWidth + ev.clientX - startX));
      setPanelWidth(w);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    e.preventDefault();
  }, []);

  return (
    <div
      ref={panelRef}
      className={`events-panel${collapsed ? ' events-panel--collapsed' : ''}`}
      style={collapsed ? undefined : { width: panelWidth }}
    >
      {collapsed ? (
        <button
          className="events-panel-expand-btn"
          onClick={() => setCollapsed(false)}
          title="Expand callback log"
        >
          ›
        </button>
      ) : (
        <>
          {/* Title makes it unambiguous this panel is rendered by the host app, not the
              FormBuilder library — it only exists because App.tsx wraps each FormBuilderCallback
              with addLog() before forwarding it to the mock store. */}
          <div className="events-panel-title">
            <span>Host App — Callback Log</span>
            <button
              className="events-panel-collapse-btn"
              onClick={() => setCollapsed(true)}
              title="Collapse callback log"
            >
              ‹
            </button>
          </div>
          <div className="log-panel">
            <div className="log-toolbar">
              <span className="log-count">{log.length} events</span>
              <button className="log-clear" onClick={onClear}>
                Clear
              </button>
            </div>
            {log.length === 0 ? (
              <div className="log-empty">No events yet. Edit, save, or publish the form to see callbacks fire.</div>
            ) : (
              log.map((entry) => {
                const isExpanded = expandedIds.has(entry.id);
                const hasPayload = entry.payload !== undefined;
                return (
                  <div
                    key={entry.id}
                    className={`log-entry${hasPayload ? ' log-entry--expandable' : ''}${isExpanded ? ' log-entry--expanded' : ''}`}
                    onClick={
                      hasPayload
                        ? () =>
                            setExpandedIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(entry.id)) next.delete(entry.id);
                              else next.add(entry.id);
                              return next;
                            })
                        : undefined
                    }
                  >
                    <span className="log-time">{entry.time}</span>
                    <span className="log-type" style={{ color: LOG_TYPE_COLORS[entry.type] ?? '#94a3b8' }}>
                      {entry.type}
                    </span>
                    <span className="log-detail">
                      {entry.detail}
                      {hasPayload && <span className="log-expand-icon">{isExpanded ? '▼' : '▶'}</span>}
                    </span>
                    {isExpanded && <pre className="log-payload">{JSON.stringify(entry.payload, null, 2)}</pre>}
                  </div>
                );
              })
            )}
          </div>
          <div className="events-panel-resize-handle" onMouseDown={handleResizeStart} />
        </>
      )}
    </div>
  );
}

function FormEditor({ store, formId, isNewForm, onBack }: { store: FormStore; formId: string; isNewForm: boolean; onBack: () => void }) {
  const { definitions, versions, latestVersionFor, saveDraft, publish, createVersion, restoreVersion, archiveVersion, updateDefinition } = store;

  const [log, setLog] = useState<LogEntry[]>([]);

  const addLog = useCallback((type: string, detail: string, payload?: unknown) => {
    setLog((prev) => [{ id: ++logCounter, time: new Date().toLocaleTimeString(), type, detail, payload }, ...prev].slice(0, 200));
  }, []);

  const definition = definitions.find((d) => d.formId === formId);
  const formVersions = versions.filter((v) => v.formId === formId);
  const activeVersion = latestVersionFor(formId);

  if (!definition || !activeVersion) {
    return (
      <div style={{ padding: 24, color: '#f1f5f9' }}>
        Form not found. <Button onClick={onBack}>Back to Library</Button>
      </div>
    );
  }

  const callbacks: FormBuilderCallbacks = {
    onSaveDraft: (version: FormVersion) => {
      addLog('SAVE_DRAFT', `v${version.versionNumber} "${definition.name}"`, version);
      saveDraft(version);
    },
    onPublish: (version: FormVersion) => {
      addLog('PUBLISH_VERSION', `v${version.versionNumber} "${definition.name}"`, { formVersionId: version.formVersionId });
      publish(version);
    },
    onCreateVersion: (fromVersionId: string) => {
      addLog('CREATE_VERSION', `from ${fromVersionId}`, { fromVersionId });
      createVersion(fromVersionId);
    },
    onRestoreVersion: (versionId: string) => {
      addLog('RESTORE_VERSION', `${versionId}`, { formVersionId: versionId });
      restoreVersion(versionId);
    },
    onArchiveVersion: (versionId: string) => {
      addLog('ARCHIVE_VERSION', `${versionId}`, { formVersionId: versionId });
      archiveVersion(versionId);
    },
    onFieldSelected: (field: Field | null) => {
      addLog('FIELD_SELECTED', field ? `${field.label} (${field.handle})` : '(none)', field ?? { selected: null });
    },
    onDirtyChange: (isDirty: boolean) => {
      addLog('DIRTY_CHANGED', String(isDirty), { isDirty });
    },
    // The embedded FormBuilder's own Toolbar renders the "← Back" button when this is
    // supplied, instead of the host needing a separate navigation bar above it.
    onNavigateBack: onBack,
    // Likewise, the embedded FormBuilder renders its own Name/Description bar when this
    // is supplied — the host just persists the patch.
    onUpdateDefinition: (patch) => updateDefinition(definition.formId, patch),
    onImportFromScreenshot: async () => {
      try {
        const doc = await importFormFromScreenshot();
        if (doc) addLog('IMPORT_SCREENSHOT', `Imported ${doc.sections.length} section(s) from screenshot`, doc);
        return doc;
      } catch (err) {
        addLog('IMPORT_SCREENSHOT', `Error: ${err instanceof Error ? err.message : String(err)}`, { error: String(err) });
        throw err;
      }
    },
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Events panel always renders on the left, matching the Fulcrum.Schedule.Embedded
          host's sidebar placement — no show/hide toggle, it's part of the host shell. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row' }}>
        <EventsPanel log={log} onClear={() => setLog([])} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <FormBuilder
            formDefinition={definition}
            versions={formVersions}
            activeVersion={activeVersion}
            callbacks={callbacks}
            isNewForm={isNewForm}
            style={{ height: '100%' }}
          />
        </div>
      </div>
    </div>
  );
}

export default function App() {
  // A single shared store instance, lifted above both screens. Previously FormLibrary and
  // FormEditor each called useFormStore() independently, so a form created in one was
  // invisible to the other until a localStorage round-trip caught up — which is why
  // "New Form" appeared to do nothing (or opened a "Form not found" editor).
  const store = useFormStore();
  const [openFormId, setOpenFormId] = useState<string | null>(null);
  const [isNewForm, setIsNewForm] = useState(false);

  return (
    <div className="app">
      <AppHeader />
      <div className="app-body">
        {openFormId ? (
          <FormEditor
            store={store}
            formId={openFormId}
            isNewForm={isNewForm}
            onBack={() => { setOpenFormId(null); setIsNewForm(false); }}
          />
        ) : (
          <FormLibrary
            definitions={store.definitions}
            callbacks={{
              onOpenForm: (formId) => { setOpenFormId(formId); setIsNewForm(false); },
              onCreateForm: () => {
                const { definition } = store.createForm('Untitled Form');
                setOpenFormId(definition.formId);
                setIsNewForm(true);
              },
              onDeleteForm: store.deleteForm,
            }}
          />
        )}
      </div>
    </div>
  );
}
